import { TTabChildItem, TTabItem } from "components/Layout/Navbar/Tab/types";
import {
  EBusinessUnit,
  ENav,
  ENavTab,
  EPersonal,
} from "components/Layout/types";
import {
  buSlugParam,
  clientMainPath,
  comSlugParam,
  corpSlugParam,
  personalSlugParam,
  staffMainPath,
} from "routes/constants";

type GenerateTabItemsParams = {
  tabList: Record<string, Record<string, string | Record<string, string>>>;
  tabLabel: Record<string, string>;
  personalSlug?: string;
  corporationSlug?: string;
  buSlug?: string;
  communitySlug?: string;
  isSuperAdmin?: boolean;
  pathname: string;
};

export const generateTabItems = ({
  tabLabel,
  tabList,
  personalSlug,
  corporationSlug,
  buSlug,
  communitySlug,
  isSuperAdmin,
  pathname,
}: GenerateTabItemsParams): TTabItem => {
  const tabItem: TTabItem = {};
  let domain = ENav.PERSONAL;
  let replacers: Array<{ key: string; slug?: string }> = [];
  let personalDomain: string = EPersonal.EMPLOYEE_OWN;
  let skipStringPath = false;

  if (communitySlug) {
    replacers = [
      { key: `:${comSlugParam}`, slug: communitySlug },
      { key: `:${buSlugParam}`, slug: buSlug },
    ];
    domain = ENav.COMMUNITY;
  } else if (buSlug) {
    replacers = [
      { key: `:${buSlugParam}`, slug: buSlug },
      { key: `:${corpSlugParam}`, slug: corporationSlug },
    ];
    domain = ENav.BUSINESS_UNIT;
  } else if (corporationSlug) {
    replacers = [{ key: `:${corpSlugParam}`, slug: corporationSlug }];
    domain = ENav.CORPORATION;
  } else if (personalSlug) {
    replacers = [{ key: `:${personalSlugParam}`, slug: personalSlug }];
    domain = ENav.PERSONAL;
  } else {
    const splitedPaths = pathname.split("/");
    if (splitedPaths?.[1] === ENav.SEARCH.toLowerCase()) {
      domain = ENav.SEARCH;
    }
    if (splitedPaths?.[1] === ENav.KENSAKU_KUN.toLowerCase()) {
      domain = ENav.KENSAKU_KUN;
    }
  }

  /**
   * If the domain is personal, need to check whether
   * the account type is employee, staff, or client
   */
  if (domain === ENav.PERSONAL) {
    if (personalSlug) {
      const currentPath = pathname;
      const splitedPaths = currentPath.split("/");

      if (splitedPaths?.[1]) {
        const path = `/${splitedPaths[1]}`;
        switch (path) {
          case staffMainPath:
            personalDomain = EPersonal.STAFF;
            break;
          case clientMainPath:
            personalDomain = EPersonal.CLIENT;
            break;
          default:
            personalDomain = EPersonal.EMPLOYEE;
            break;
        }
      }
    } else {
      if (isSuperAdmin) {
        personalDomain = EPersonal.SUPERADMIN;
      }
    }
  } else if (domain === ENav.BUSINESS_UNIT) {
    const currentPath = pathname;
    const splitedPaths = currentPath.split("/");

    if (splitedPaths?.[4] === "manage") {
      personalDomain = EBusinessUnit.ASSIGNMENT;
      skipStringPath = true;
    }
  }

  for (const key in tabList) {
    if (tabList?.[key]?.[domain] === undefined) continue;

    const pathValue = tabList[key][domain];

    const DROPDOWN_TABS = new Set<string>([ENavTab.EVALUATE]);
    if (DROPDOWN_TABS.has(key as ENavTab) && typeof pathValue === "object") {
      const children: Record<string, TTabChildItem> = {};

      for (const [childKey, rawChildPath] of Object.entries(pathValue)) {
        if (typeof rawChildPath !== "string") continue;

        let childPath = rawChildPath;

        for (const replacer of replacers) {
          childPath = childPath.replace(
            replacer.key || "",
            replacer.slug || ""
          );
        }

        children[childKey] = {
          label: tabLabel?.[childKey] || "",
          path: childPath,
          iconKey: childKey as ENavTab,
        };
      }

      if (Object.keys(children).length > 0) {
        tabItem[key] = {
          label: tabLabel?.[key] || "",
          children,
        };
      }

      continue;
    }

    if (typeof pathValue === "string") {
      if (skipStringPath) continue;

      let path = pathValue;
      for (const replacer of replacers) {
        path = path.replace(replacer.key || "", replacer.slug || "");
      }

      tabItem[key] = {
        label: tabLabel?.[key] || "",
        path,
      };

      continue;
    }

    if (pathValue?.[personalDomain]) {
      let path = pathValue[personalDomain];

      for (const replacer of replacers) {
        path = path.replace(replacer.key || "", replacer.slug || "");
      }

      tabItem[key] = {
        label: tabLabel?.[key] || "",
        path,
      };

      continue;
    }
  }

  return tabItem;
};
